<template>
  <form
    class="predictive-search-bar"
    role="search"
    @submit.prevent="onSubmit"
  >
    <div class="predictive-search-bar__input-wrapper">
      <span
        class="predictive-search-bar__icon"
      >
        <icon-search />
      </span>
      <input
        ref="input"
        v-model="input"
        class="predictive-search-bar__input"
        type="text"
        :placeholder="$string('search.search')"
        autocomplete="off"
        @keyup.enter="onSubmit"
      >
      <a
        v-show="input.length > 0"
        class="predictive-search-bar__clear"
        tabindex="0"
        @click="clearSearch"
        @keyup.enter="clearSearch"
        v-html="$string('search.clear')"
      />
    </div>

    <div class="predictive-search-bar__divider" />

    <div
      v-show="query.length && suggestions.length"
      class="predictive-search-bar__suggestions"
    >
      <span
        class="predictive-search-bar__suggestions-header"
        v-html="formatTranslation($string('search.did_you_mean'))"
      />
      <ul
        class="predictive-search-bar__suggestions-wrapper"
      >
        <li
          v-for="suggestion of suggestions"
          :key="suggestion"
          class="predictive-search-bar__suggestions-suggestion"
        >
          <a
            :href="`/search?q=${suggestion}`"
            v-html="highlightQueryMatch(suggestion)"
          />
        </li>
      </ul>
    </div>

    <div
      v-show="query.length"
      class="predictive-search-bar__results"
    >
      <span
        v-if="noResults"
        class="predictive-search-bar__no-results"
        v-html="formatTranslation($string('search.no_results'))"
      />

      <predictive-search-results-section
        v-for="resultType of includedResultTypes"
        :key="resultType"
        ref="resultsSections"
        :result-type="resultType"
      />
    </div>
    <boost-api-call
      :query="query"
      :endpoint="endpoint"
      :limit="3"
      :shop="shop"
      event-type="suggest_dym"
      @boost-updated="boostUpdated"
    />

    <boost-analytics
      :analytics-event="analyticsEvent"
      type="search"
    />
  </form>

  <div
    v-show="showPopularCategories"
    class="predictive-search-bar__popular-categories"
  >
    <div
      class="predictive-search-bar__popular-categories-header"
      v-html="$string('search.popular_categories')"
    />

    <ul
      class="predictive-search-bar__card-grid collections"
    >
      <li
        v-for="collection of popularCategories"
        :key="collection.title"
      >
        <a
          class="predictive-search-bar__result"
          :href="collection.url"
        >
          <responsive-image
            v-if="collection.image"
            :image="collection.image"
          />
          <div class="predictive-search-bar__collection-card-footer">
            <btn
              class="predictive-search-bar__collection-card-title"
              :label="collection.title"
              :show-icon="false"
              modifiers="text"
              tabindex="0"
            />
          </div>
        </a>
      </li>
    </ul>
  </div>
</template>

<script>

/**
 * Vue: Predictive search bar (predictive-search-bar)
 * -----------------------------------------------------------------------------
 * The predictive search bar in the site header.
 * @param {Boolean} isActive - if modal is active
 * @param {Boolean} includeCollections
 * @param {Boolean} includePages
 * @param {Array} popularCategories - collections set in Site Header block

 */

import BoostAnalytics from '~global/boost-analytics/boost-analytics'
import BoostApiCall from '~global/boost-api-call/boost-api-call'
import Btn from '~global/btn/btn'
import ResponsiveImage from '~global/images/responsive-image'
import IconSearch from '~icons/misc/search.svg'
import PredictiveSearchResultsSection from './predictive-search-results-section'

import { formatBoostProduct } from '~/helpers/format'

export default {
  name: 'PredictiveSearchBar',

  components: {
    BoostAnalytics,
    BoostApiCall,
    Btn,
    IconSearch,
    PredictiveSearchResultsSection,
    ResponsiveImage,
  },

  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
    includeCollections: {
      type: Boolean,
      default: true,
    },
    includePages: {
      type: Boolean,
      default: false,
    },
    includeProducts: {
      type: Boolean,
      default: true,
    },
    popularCategories: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      analyticsEvent: {},
      input: '',
      query: '',
      redirect: '',
      allEmpty: true,
      totalProducts: 0,
      collections: [],
      products: [],
      pages: [],
      suggestions: [],
    }
  },

  computed: {

    /**
     * Set to use the 'search/suggest' endpoint for the API.
     * @returns {String}
     */
    endpoint() {
      return 'search/suggest'
    },

    /**
     * Returns an array of result types to be included, based on props.
     * @returns {Array}
     */
    includedResultTypes() {
      const resultTypes = []

      if (this.includePages) {
        resultTypes.push('pages')
      }
      if (this.includeCollections) {
        resultTypes.push('collections')
      }
      if (this.includeProducts) {
        resultTypes.push('products')
      }

      return resultTypes
    },

    /**
     * Compute if there are no results.
     * @returns {Boolean}
     */
    noResults() {
      return this.query.length > 0 && this.allEmpty
    },

    /**
     * Returns address for search results page
     * @returns {String}
     */
    searchResultsPage() {
      if (this.redirect) {
        return this.redirect
      }
      return `/search?q=${this.query}`
    },

    /**
     * Returns shop URL
     * @returns {String}
     */
    shop() {
      return this.$variable('store.permanentDomain').replaceAll('/', '')
    },

    /**
     * Compute whether or not to show popular categories section.
     * @returns {Boolean}
     */
    showPopularCategories() {
      if (!this.popularCategories.length) {
        return false
      }

      if (this.query.length && !this.noResults) {
        return false
      }

      return true
    },
  },

  watch: {

    /**
     * Focus input when search overlay is made active.
     * @param {Boolean} value
     */
    isActive(value) {
      if (value) {
        setTimeout(() => {
          this.$refs.input.focus()
        }, 500)
      }
    },

    /**
     * Watches as the search bar input changes.
     * @param {String} value
     */
    input(value) {
      this.query = value
      cnvs.EventBus.emit('update-query', value)
    },
  },

  mounted() {
    this.initQuery()
  },

  methods: {

    /**
     * Format translation by replacing placeholder with query value.
     * @param {String} translation
     * @returns {String}
     */
    formatTranslation(translation) {
      return `${translation.replace(
        '"{{ query }}"',
        `<b>"${this.query}"</b>`,
      )}`
    },

    /**
     * Initialize query from current search parameters on load.
     */
    initQuery() {
      const params = new URLSearchParams(window.location.search)
      const query = params.get('q')

      if (!query) {
        return
      }

      this.input = query
    },

    /**
     * Remove any existing bold tags, then wrap each query match in a bold tag.
     * @param {String} string
     * @returns {String}
     */
    highlightQueryMatch(string) {
      return string.replace(/<\/?b>/gi, '')
        .replace(new RegExp(this.query, 'gi'), (match) => `<b>${match}</b>`)
    },

    /**
     * Loop through suggestions and results to highlight matches.
     */
    highlightQueryMatches() {
      for (const section of this.$refs.resultsSections) {
        if (section.$refs.resultStrings) {
          for (const result of section.$refs.resultStrings) {
            result.innerHTML = this.highlightQueryMatch(result.innerHTML)
          }
        }
      }
    },

    /**
     * Clears query string, clears and focuses the input.
     */
    clearSearch() {
      this.query = ''
      this.input = ''
      this.$refs.input.focus()
    },

    /**
     * Redirects browser to the results page.
     */
    onSubmit() {
      window.location = this.searchResultsPage
    },

    /**
     * Callback for Boost API call.
     * @param {Object} response - Response from API call.
     */
    boostUpdated(response) {
      this.allEmpty = response.all_empty
      this.totalProducts = response.total_product
      this.redirect = response.redirect ? response.redirect : false
      this.suggestions = response.suggestions

      for (const resultType of this.includedResultTypes) {
        this[resultType] = response[resultType]
      }

      this.products = response.products.map((product) => {
        return formatBoostProduct(product)
      })

      this.highlightQueryMatches()
    },
  },
}
</script>

<style lang="scss">
  @import './predictive-search-bar';
</style>
